import React, { useEffect, useState } from "react";
import { Alert, Button, Checkbox, Divider, Form, Input, Select, Space, Typography } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { useParams } from "react-router-dom";
import { SectionPanelProps } from "./SectionTypes";
import { useClientConfigService } from "../../../services/clients/ClientConfigServiceProvider";
import {
    DEFAULT_SIGNUP_ENQUIRY_TYPES,
    DEFAULT_SIGNUP_GUEST_TYPES,
    DEFAULT_SIGNUP_HOW_THEY_HEARD,
    DEFAULT_SIGNUP_INTERESTS,
    GUEST_TYPE_VALUES,
    SignupOption,
} from "../../../model/client/ClientSettings";

const { Text, Paragraph } = Typography;

export interface SignupSectionValue {
    collectCompanyName: boolean;
    membershipPricingSheetKey: string;
    interests: SignupOption[];
    howTheyHeard: SignupOption[];
    guestTypes: SignupOption[];
    enquiryTypes: SignupOption[];
    directDebitRequired: boolean;
}

export const DEFAULT_SIGNUP: SignupSectionValue = {
    collectCompanyName: false,
    membershipPricingSheetKey: "",
    interests: DEFAULT_SIGNUP_INTERESTS,
    howTheyHeard: DEFAULT_SIGNUP_HOW_THEY_HEARD,
    guestTypes: DEFAULT_SIGNUP_GUEST_TYPES,
    enquiryTypes: DEFAULT_SIGNUP_ENQUIRY_TYPES,
    directDebitRequired: false,
};

// Slash-separated S3 object key (private assets bucket). Mirrors the backend
// ClientService.IsValidAssetKey check: a safe character set, no leading/trailing
// slash, no "." / ".." segments. Empty is allowed (clears the pricing sheet).
const KEY_PATTERN = /^[A-Za-z0-9._-]+(?:\/[A-Za-z0-9._-]+)*$/;

export function isValidAssetKey(key: string): boolean {
    const k = key.trim();
    if (k.length === 0) return true;
    if (!KEY_PATTERN.test(k)) return false;
    return !k.split("/").some((segment) => segment === "." || segment === "..");
}

// Validates one option list the same way the backend does (non-empty, unique,
// within the allowed value set when constrained). Returns an error string or
// null. Mirrors SignupSettings.TryCreateOptions so the operator sees the failure
// before the PUT round-trips.
export function validateOptionList(
    options: SignupOption[],
    allowedValues?: readonly string[],
): string | null {
    if (options.length === 0) return "Add at least one option.";
    const seen = new Set<string>();
    for (const option of options) {
        const value = option.value.trim();
        if (value.length === 0) return "Every option needs a value.";
        if (allowedValues && !allowedValues.includes(value)) {
            return `"${value}" is not a valid guest type.`;
        }
        const key = value.toLowerCase();
        if (seen.has(key)) return `Duplicate option value "${value}".`;
        seen.add(key);
    }
    return null;
}

// Trims value/label and drops rows that are entirely blank, so a half-added row
// doesn't block save or persist noise.
export function normaliseOptions(options: SignupOption[]): SignupOption[] {
    return options
        .map((o) => ({ value: o.value.trim(), label: o.label.trim() }))
        .filter((o) => o.value.length > 0 || o.label.length > 0);
}

export interface OptionListEditorProps {
    title: string;
    description: string;
    testId: string;
    options: SignupOption[];
    onChange: (next: SignupOption[]) => void;
    // When set, the value field becomes a constrained Select of these values
    // (used for the guest/enquiry-type lists, which must be GuestType names).
    valueChoices?: readonly string[];
    valuePlaceholder: string;
    labelPlaceholder: string;
    readOnly?: boolean;
    error?: string | null;
}

export const OptionListEditor: React.FC<OptionListEditorProps> = ({
    title,
    description,
    testId,
    options,
    onChange,
    valueChoices,
    valuePlaceholder,
    labelPlaceholder,
    readOnly,
    error,
}) => {
    const updateAt = (index: number, patch: Partial<SignupOption>) =>
        onChange(options.map((o, i) => (i === index ? { ...o, ...patch } : o)));
    const removeAt = (index: number) => onChange(options.filter((_, i) => i !== index));
    const add = () => onChange([...options, { value: "", label: "" }]);

    return (
        <div data-testid={testId}>
            <Text strong>{title}</Text>
            <Paragraph type="secondary" style={{ marginBottom: 8 }}>
                {description}
            </Paragraph>
            <Space direction="vertical" size="small" style={{ width: "100%" }}>
                {options.map((option, index) => (
                    <Space key={index} align="start" style={{ width: "100%" }} wrap>
                        {valueChoices ? (
                            <Select
                                data-testid={`${testId}-value-${index}`}
                                style={{ width: 160 }}
                                placeholder={valuePlaceholder}
                                value={option.value || undefined}
                                disabled={readOnly}
                                options={valueChoices.map((v) => ({ value: v, label: v }))}
                                onChange={(v) => updateAt(index, { value: v })}
                            />
                        ) : (
                            <Input
                                data-testid={`${testId}-value-${index}`}
                                style={{ width: 200 }}
                                placeholder={valuePlaceholder}
                                value={option.value}
                                disabled={readOnly}
                                onChange={(e) => updateAt(index, { value: e.target.value })}
                            />
                        )}
                        <Input
                            data-testid={`${testId}-label-${index}`}
                            style={{ width: 220 }}
                            placeholder={labelPlaceholder}
                            value={option.label}
                            disabled={readOnly}
                            onChange={(e) => updateAt(index, { label: e.target.value })}
                        />
                        <Button
                            data-testid={`${testId}-remove-${index}`}
                            icon={<DeleteOutlined />}
                            aria-label={`Remove option ${index + 1}`}
                            disabled={readOnly}
                            onClick={() => removeAt(index)}
                        />
                    </Space>
                ))}
                <Button
                    data-testid={`${testId}-add`}
                    icon={<PlusOutlined />}
                    disabled={readOnly}
                    onClick={add}
                >
                    Add option
                </Button>
                {error && <Alert type="error" showIcon message={error} data-testid={`${testId}-error`} />}
            </Space>
        </div>
    );
};

// Controlled signup-form section: toggles the optional Company field, holds the
// S3 key of the Tour pricing-sheet image, and edits the per-client option lists
// (interests, how-they-heard, guest types, enquiry types) shown on the guest and
// tour signup forms.
export const SignupSection: React.FC<SectionPanelProps<SignupSectionValue>> = ({
    value,
    onChange,
    readOnly,
}) => {
    const keyError = value.membershipPricingSheetKey.trim().length > 0
        && !isValidAssetKey(value.membershipPricingSheetKey);
    return (
        <Form layout="vertical" data-testid="settings-signup-form" disabled={readOnly}>
            <Form.Item>
                <Checkbox
                    data-testid="settings-signup-collect-company"
                    checked={value.collectCompanyName}
                    onChange={(e) => onChange({ ...value, collectCompanyName: e.target.checked })}
                >
                    Collect company name on signup
                </Checkbox>
            </Form.Item>

            <Form.Item
                help={
                    "When on, members signing up online can't skip the Direct Debit — the \"set it up later\" "
                    + "option is removed and the mandate is required. Staff creating a member are not affected."
                }
            >
                <Checkbox
                    data-testid="settings-signup-direct-debit-required"
                    checked={value.directDebitRequired}
                    onChange={(e) => onChange({ ...value, directDebitRequired: e.target.checked })}
                >
                    Require a Direct Debit when members sign up
                </Checkbox>
            </Form.Item>

            <Form.Item
                label="Membership pricing sheet (S3 key)"
                help={
                    keyError
                        ? "Enter a plain object key like documents/club/pricing.png (no leading slash, no spaces)."
                        : "Object key of the pricing sheet image (PNG/JPG) in the private assets bucket. Shown on the guest form when a Tour guest is signed up. Leave blank to hide it."
                }
                validateStatus={keyError ? "error" : undefined}
            >
                <Input
                    data-testid="settings-signup-pricing-key"
                    placeholder="documents/club/pricing.png"
                    value={value.membershipPricingSheetKey}
                    onChange={(e) => onChange({ ...value, membershipPricingSheetKey: e.target.value })}
                />
            </Form.Item>

            <Divider />

            <OptionListEditor
                title="Interests"
                description="Checkboxes on the guest signup form. Value is stored on the member; label is shown to the operator."
                testId="settings-signup-interests"
                options={value.interests}
                onChange={(interests) => onChange({ ...value, interests })}
                valuePlaceholder="Stored value"
                labelPlaceholder="Shown label"
                readOnly={readOnly}
                error={validateOptionList(value.interests)}
            />

            <Divider />

            <OptionListEditor
                title="How they heard"
                description="Radio options for how the guest heard about the club."
                testId="settings-signup-how-they-heard"
                options={value.howTheyHeard}
                onChange={(howTheyHeard) => onChange({ ...value, howTheyHeard })}
                valuePlaceholder="Stored code"
                labelPlaceholder="Shown label"
                readOnly={readOnly}
                error={validateOptionList(value.howTheyHeard)}
            />

            <Divider />

            <OptionListEditor
                title="Guest types (guest signup)"
                description="Guest-type choices on the guest signup form. Value must be a system guest type; relabel it freely."
                testId="settings-signup-guest-types"
                options={value.guestTypes}
                onChange={(guestTypes) => onChange({ ...value, guestTypes })}
                valueChoices={GUEST_TYPE_VALUES}
                valuePlaceholder="Guest type"
                labelPlaceholder="Shown label"
                readOnly={readOnly}
                error={validateOptionList(value.guestTypes, GUEST_TYPE_VALUES)}
            />

            <Divider />

            <OptionListEditor
                title="Enquiry types (tour signup)"
                description="Enquiry-type choices on the tour signup form. Value must be a system guest type; relabel it freely."
                testId="settings-signup-enquiry-types"
                options={value.enquiryTypes}
                onChange={(enquiryTypes) => onChange({ ...value, enquiryTypes })}
                valueChoices={GUEST_TYPE_VALUES}
                valuePlaceholder="Guest type"
                labelPlaceholder="Shown label"
                readOnly={readOnly}
                error={validateOptionList(value.enquiryTypes, GUEST_TYPE_VALUES)}
            />
        </Form>
    );
};

// Adapter that mounts SignupSection inside the Settings shell: loads current
// signup config from /config, tracks dirty state, and saves via PUT.
export const SignupSectionPanel: React.FC<SectionPanelProps<unknown>> = () => {
    const service = useClientConfigService();
    const { clientId: clientIdParam } = useParams<{ clientId: string }>();
    const clientId = clientIdParam !== undefined ? Number(clientIdParam) : NaN;
    const [initial, setInitial] = useState<SignupSectionValue | null>(null);
    const [value, setValue] = useState<SignupSectionValue>(DEFAULT_SIGNUP);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!Number.isFinite(clientId)) {
            setLoadError("Invalid client id in URL.");
            return;
        }
        setLoadError(null);
        let cancelled = false;
        (async () => {
            try {
                const config = await service.getConfig(clientId);
                if (cancelled) return;
                const signup = config?.settings.signup;
                const loaded: SignupSectionValue = {
                    collectCompanyName: signup?.collectCompanyName ?? DEFAULT_SIGNUP.collectCompanyName,
                    directDebitRequired: signup?.directDebitRequired ?? DEFAULT_SIGNUP.directDebitRequired,
                    membershipPricingSheetKey:
                        signup?.membershipPricingSheetKey ?? DEFAULT_SIGNUP.membershipPricingSheetKey,
                    interests: signup?.interests ?? DEFAULT_SIGNUP.interests,
                    howTheyHeard: signup?.howTheyHeard ?? DEFAULT_SIGNUP.howTheyHeard,
                    guestTypes: signup?.guestTypes ?? DEFAULT_SIGNUP.guestTypes,
                    enquiryTypes: signup?.enquiryTypes ?? DEFAULT_SIGNUP.enquiryTypes,
                };
                setInitial(loaded);
                setValue(loaded);
            } catch (err) {
                if (!cancelled) setLoadError(err instanceof Error ? err.message : "Failed to load settings.");
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [service, clientId]);

    const dirty = initial !== null && JSON.stringify(initial) !== JSON.stringify(value);
    const optionsValid =
        validateOptionList(value.interests) === null
        && validateOptionList(value.howTheyHeard) === null
        && validateOptionList(value.guestTypes, GUEST_TYPE_VALUES) === null
        && validateOptionList(value.enquiryTypes, GUEST_TYPE_VALUES) === null;
    const valid = isValidAssetKey(value.membershipPricingSheetKey) && optionsValid;

    const handleSave = async () => {
        if (!Number.isFinite(clientId) || !valid) return;
        setSaving(true);
        setSaveError(null);
        const saved: SignupSectionValue = {
            collectCompanyName: value.collectCompanyName,
            directDebitRequired: value.directDebitRequired,
            membershipPricingSheetKey: value.membershipPricingSheetKey.trim(),
            interests: normaliseOptions(value.interests),
            howTheyHeard: normaliseOptions(value.howTheyHeard),
            guestTypes: normaliseOptions(value.guestTypes),
            enquiryTypes: normaliseOptions(value.enquiryTypes),
        };
        try {
            await service.updateSignup(clientId, saved);
            setInitial(saved);
            setValue(saved);
        } catch (err) {
            setSaveError(err instanceof Error ? err.message : "Failed to save settings.");
        } finally {
            setSaving(false);
        }
    };

    if (loadError) {
        return <Alert type="error" message={loadError} data-testid="settings-signup-load-error" />;
    }
    if (initial === null) {
        return <Text type="secondary">Loading…</Text>;
    }

    return (
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                Controls what the guest and tour signup forms collect and show.
            </Paragraph>
            <SignupSection value={value} onChange={setValue} />
            {saveError && <Alert type="error" message={saveError} data-testid="settings-signup-save-error" />}
            <Space>
                <Button
                    type="primary"
                    data-testid="settings-signup-save-btn"
                    onClick={handleSave}
                    loading={saving}
                    disabled={!dirty || !valid}
                >
                    Save changes
                </Button>
                <Button
                    data-testid="settings-signup-discard-btn"
                    onClick={() => initial && setValue(initial)}
                    disabled={!dirty || saving}
                >
                    Discard
                </Button>
            </Space>
        </Space>
    );
};
